/**
 * SurveysOverview widget — rendered inline in chat by the agent.
 *
 * Displays a sortable table of available surveys, grouped by country.
 * The agent calls `render_surveys_overview(surveys=[...])` and this
 * component renders the data visually with clickable column headers
 * for sorting.
 */

"use client"

import { useState, useMemo } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react"

interface SurveyItem {
  surveyId: string
  country: string
  year: number
  program: string
}

/** The columns available for sorting. */
type SortKey = keyof SurveyItem

/** Sort direction state. */
type SortDirection = "asc" | "desc"

interface SurveysOverviewProps {
  /** Array of survey objects to display. */
  surveys: SurveyItem[]
  /** Optional title override for the card header. */
  title?: string
}

/**
 * Renders a sortable table of surveys with country, year, program, and ID.
 * Click any column header to sort by that column. Click again to toggle
 * between ascending and descending order.
 *
 * @param props.surveys - Array of survey items to display in the table.
 * @param props.title - Optional card title (defaults to "Available Surveys").
 * @returns A Card component with a responsive, sortable survey table.
 */
export function SurveysOverview({ surveys, title }: SurveysOverviewProps) {
  const [sortKey, setSortKey] = useState<SortKey>("country")
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc")

  /**
   * Handle a column header click. If already sorting by this column, toggle
   * direction. Otherwise, sort ascending by the new column.
   */
  function handleSort(key: SortKey): void {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))
    } else {
      setSortKey(key)
      setSortDirection("asc")
    }
  }

  /** Sorted copy of the surveys array based on current sort state. */
  const sortedSurveys = useMemo(() => {
    if (!surveys || surveys.length === 0) return []

    return [...surveys].sort((a, b) => {
      const aVal = a[sortKey]
      const bVal = b[sortKey]

      // Numeric comparison for year, string comparison for everything else.
      let comparison: number
      if (typeof aVal === "number" && typeof bVal === "number") {
        comparison = aVal - bVal
      } else {
        comparison = String(aVal).localeCompare(String(bVal))
      }

      return sortDirection === "asc" ? comparison : -comparison
    })
  }, [surveys, sortKey, sortDirection])

  /**
   * Render the sort indicator icon for a given column.
   * Shows directional arrow if active, neutral icon if inactive.
   */
  function SortIcon({ columnKey }: { columnKey: SortKey }) {
    if (sortKey !== columnKey) {
      return <ArrowUpDown className="inline h-3 w-3 ml-1 text-muted-foreground/50" />
    }
    if (sortDirection === "asc") {
      return <ArrowUp className="inline h-3 w-3 ml-1 text-primary" />
    }
    return <ArrowDown className="inline h-3 w-3 ml-1 text-primary" />
  }

  if (!surveys || surveys.length === 0) {
    return (
      <Card className="my-2 border-muted shadow-sm max-w-2xl">
        <CardContent className="py-4 text-sm text-muted-foreground">
          No surveys found.
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="my-2 border-primary/20 shadow-sm max-w-2xl">
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="text-base font-semibold">
          {title ?? "Available Surveys"}
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-0.5">
          {surveys.length} survey{surveys.length !== 1 ? "s" : ""} found
        </p>
      </CardHeader>
      <CardContent className="px-4 pb-3 pt-0">
        <div className="max-h-72 overflow-y-auto rounded border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead
                  className="text-xs cursor-pointer select-none hover:bg-muted/50 transition-colors"
                  onClick={() => handleSort("country")}
                >
                  Country
                  <SortIcon columnKey="country" />
                </TableHead>
                <TableHead
                  className="text-xs cursor-pointer select-none hover:bg-muted/50 transition-colors"
                  onClick={() => handleSort("year")}
                >
                  Year
                  <SortIcon columnKey="year" />
                </TableHead>
                <TableHead
                  className="text-xs cursor-pointer select-none hover:bg-muted/50 transition-colors"
                  onClick={() => handleSort("program")}
                >
                  Program
                  <SortIcon columnKey="program" />
                </TableHead>
                <TableHead
                  className="text-xs cursor-pointer select-none hover:bg-muted/50 transition-colors"
                  onClick={() => handleSort("surveyId")}
                >
                  Survey ID
                  <SortIcon columnKey="surveyId" />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedSurveys.map((survey, index) => (
                <TableRow key={`${survey.surveyId}-${index}`}>
                  <TableCell className="text-xs py-1.5">
                    {survey.country}
                  </TableCell>
                  <TableCell className="text-xs py-1.5">
                    {survey.year}
                  </TableCell>
                  <TableCell className="text-xs py-1.5">
                    <Badge variant="secondary" className="text-[10px]">
                      {survey.program}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs py-1.5 font-mono">
                    {survey.surveyId}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}
